import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Flex,
  Heading,
  Text,
  Button,
  Badge,
  Separator,
  Table,
} from "@radix-ui/themes";
import { DownloadIcon, ExternalLinkIcon } from "@radix-ui/react-icons";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import {
  formatCurrency,
  formatQuantity,
  formatDate,
  useBillingApi,
} from "@/v2/entities/billing/Billing.api";
import type { UsageData } from "@/v2/entities/billing/Billing.model";
import Select from "@/components/shared/select";
import { PlanUpgradeModal } from "./PlanUpgradeModal";
import styles from "./Billing.module.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useGetTenantStatus } from "@/v2/entities/tenant-status/TenantStatus.api";

const USAGE_METER_GROUPING = "day";

export function Billing() {
  const billingEnabled = useFlag("billingEnabled", false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [usageParams, setUsageParams] = useState<{ startTime?: number; endTime?: number; period?: string }>({
    period: undefined,
  });

  const { user } = useAuth0();
  const customerId = user?.owner;
  const { workspace } = useWorkspace();
  useGetTenantStatus(workspace ?? '');
  //
  const billingApi = useBillingApi({
    ...usageParams,
    grouping: USAGE_METER_GROUPING,
  });
  
  const { usage, invoices, openCustomerPortal } = billingApi;
  const useData = usage.data;
  const usageLoading = usage.isLoading;
  const invoicesLoading = invoices.isLoading;
  const refetchInvoices = invoices.refetch;
  
  const buildPeriodKey = useCallback((period: UsageData["period"]) => period.label, []);

  // Set initial period when usage data first loads
  useEffect(() => {
    if (useData?.period && !selectedPeriod) {
      setSelectedPeriod(buildPeriodKey(useData.period));
      setUsageParams((prev) => ({
        ...prev,
        period: useData.period.label,
        startTime: useData.period.startTime,
        endTime: useData.period.endTime,
      }));
    }
  }, [useData, selectedPeriod, buildPeriodKey]);

  const periodOptions = useMemo(() => {
    if (!useData?.availablePeriods) {
      return [];
    }

    return useData.availablePeriods.map((period) => ({
      label: period.label,
      value: buildPeriodKey(period),
    }));
  }, [useData?.availablePeriods, buildPeriodKey]);

  const handlePeriodChange = useCallback(
    (periodLabel: string) => {
      setSelectedPeriod(periodLabel);
      const period = useData?.availablePeriods.find((item) => buildPeriodKey(item) === periodLabel);
      if (!period) {
        return;
      }
      setUsageParams({
        period: period.label,
        startTime: period.startTime,
        endTime: period.endTime,
      });
    },
    [useData?.availablePeriods, buildPeriodKey],
  );

  const handleOpenCustomerPortal = useCallback((customerId: string) => {
    openCustomerPortal(customerId ?? "");
  }, [openCustomerPortal, customerId]);

  const handleViewInvoice = useCallback(
    (invoiceUrl?: string | null) => {
      if (!invoiceUrl) {
        return;
      }
      window.open(invoiceUrl, "_blank", "noopener,noreferrer");
    },
    [],
  );

  const handleDownloadInvoice = useCallback(
    (invoiceId: string) => {
      refetchInvoices().then((result) => {
        const invoice = result.data?.find((inv) => inv.id === invoiceId);
        if (invoice?.invoice_pdf) {
          window.open(invoice.invoice_pdf, "_blank");
        }
      });
    },
    [refetchInvoices],
  );

  if (!billingEnabled) {
    return null;
  }

  const isInitialLoading = (usageLoading || invoicesLoading) && !useData;

  if (isInitialLoading) {
    return (
      <Box className={styles.container}>
        <Flex direction="column" gap="2">
          <Heading size="6">Billing & Invoicing</Heading>
          <Text color="gray" size="3">
            Loading...
          </Text>
        </Flex>
      </Box>
    );
  }

  const includedUsageRows = useData?.breakdown ?? [];
  const periodLabel = useData?.period.label ?? "Current period";
  const totalIncluded = formatQuantity(useData?.totals.value);

  return (
    <Box className={styles.container}>
      <Flex direction="column" gap="5">
        <Flex direction="column" gap="2">
          <Heading size="6">Billing & Invoicing</Heading>
          <Text color="gray" size="3">
            Manage your subscription and view invoices
          </Text>
        </Flex>

        <Flex align="center" justify="between" wrap="wrap" gap="3">
          <Select
            size="small"
            options={periodOptions}
            value={selectedPeriod}
            onChange={handlePeriodChange}
            loading={usageLoading}
            disabled={periodOptions.length === 0}
            placeholder="Select period"
          ></Select>

          <Flex gap="2" align="center" wrap="wrap">
          
            <Button
              variant="outline"
              size="1"
              color="gray"
              highContrast
              onClick={() => handleOpenCustomerPortal(customerId)}
            >
              <ExternalLinkIcon />
              Manage Subscription
            </Button>
          </Flex>
        </Flex>

        <Card>
          <Flex direction="column" gap="4">
            <Flex align="center" justify="between" wrap="wrap" gap="3">
              <Box>
                <Text size="1" color="gray">
                  {periodLabel} • Included usage
                </Text>
                <Flex align="baseline" gap="2" mt="1">
                  <Text size="8" weight="bold">
                    {totalIncluded}
                  </Text>
                  <Text size="3" color="gray">
                    interactions
                  </Text>
                </Flex>
              </Box>

              
            </Flex>

            <Separator />

            {usageLoading && (
              <Text size="2" color="gray">
                Updating usage…
              </Text>
            )}

            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Item</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Quantity</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {includedUsageRows.length > 0 ? (
                  includedUsageRows?.map((item) => {
                    const quantityLabel = item.unitLabel
                      ? `${formatQuantity(item.quantity)} ${item.unitLabel}`
                      : formatQuantity(item.quantity);
                    return (
                      <Table.Row key={item.label}>
                        <Table.Cell>
                          <Text size="2">{item.label}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text size="2">{quantityLabel}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          {item.included ? (
                            <Badge color="green" variant="soft" size="1">
                              Included
                            </Badge>
                          ) : (
                            <Text size="2" weight="bold">
                              {formatCurrency(item.costCents)}
                            </Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Text size="2" color="gray">
                        No usage data available for this period.
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>

            <Separator />

            <Flex justify="end">
              <Text size="2" weight="bold">
                Total included interactions: {totalIncluded}
              </Text>
            </Flex>
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="4">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Amount</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Invoice</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {invoices.data?.length ? (
                  invoices.data.map((invoice) => (
                    <Table.Row key={invoice.id}>
                      <Table.Cell>
                        <Text size="2">{formatDate(invoice.created)}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{invoice.description}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={
                            invoice.status === "paid"
                              ? "green"
                              : invoice.status === "open"
                              ? "orange"
                              : "red"
                          }
                          variant="soft"
                          size="1"
                        >
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">
                          {formatCurrency(invoice.amount)} {invoice.currency.toUpperCase()}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="1">
                          <Button
                            variant="ghost"
                            size="1"
                            color="gray"
                            highContrast
                          onClick={() => handleViewInvoice(invoice.hosted_invoice_url)}
                            title="View Invoice"
                          >
                            <ExternalLinkIcon />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="1"
                            color="gray"
                            highContrast
                            onClick={() => handleDownloadInvoice(invoice.id)}
                            title="Download PDF"
                          >
                            <DownloadIcon />
                          </Button>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <Text size="2" color="gray">
                        No invoices available for this period.
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Flex>
        </Card>
      </Flex>
    </Box>
  );
}
